import { DateTime } from "luxon";
import type { MentorshipDetails } from "@/lib/api/mentorships";
import type { D7TriggerQaScenario } from "@/lib/hooks/admin/d7-trigger-qa-scenario";
import { isMagicMomentBannerExpired } from "@/lib/utils/magic-moments";
import {
  opportunityActionKind,
  type ActionSuggestionKind,
} from "../action/context/action-suggestion";
import type { DismissedKindsByMentorship } from "../action/context/use-action-suggestion-dismissals";
import { applyD7TriggerQaToNoticeSignals } from "../d7-trigger-qa";
import type { NoticeMentorshipSignals } from "./notice-badge";
import { isTeenBirthdayToday } from "./teen-birthday";

type MagicMoment = NonNullable<MentorshipDetails["metadata"]>["magic_moment"];

function resolveDayState(
  magicMoment: MagicMoment,
  now: DateTime,
): NoticeMentorshipSignals["dayState"] {
  if (magicMoment?.day_state === "day1") {
    return "day1";
  }
  if (
    magicMoment?.day_state === "day2" &&
    !isMagicMomentBannerExpired(
      magicMoment.day_state,
      magicMoment.expires_at,
      now,
    )
  ) {
    return "day2";
  }
  return null;
}

export function buildNoticeMentorshipSignals(
  mentorship: MentorshipDetails,
  now: DateTime = DateTime.now(),
  dismissedKinds: readonly ActionSuggestionKind[] = [],
): NoticeMentorshipSignals {
  const noticeSignals = mentorship.notice_signals;
  const mentorMessagedToday = noticeSignals?.mentor_messaged_today ?? false;
  const opportunityActivity = noticeSignals?.opportunity_activity ?? null;
  const opportunityActivityKind =
    opportunityActivity &&
    !dismissedKinds.includes(opportunityActionKind(opportunityActivity))
      ? opportunityActivity
      : null;
  const momentHubKind = noticeSignals?.activity_pending
    ? "activity"
    : noticeSignals?.hub_content_pending
      ? "content"
      : noticeSignals?.moment_pending
        ? "moment"
        : null;

  return {
    birthday:
      isTeenBirthdayToday(mentorship.mentee?.birthday_md, now) &&
      !mentorMessagedToday,
    momentHubKind,
    dayState: resolveDayState(mentorship.metadata?.magic_moment, now),
    momentum: mentorship.metadata?.at_risk_momentum?.status === "active",
    opportunityActivityKind,
    opportunityHub:
      (noticeSignals?.opportunity_hub ?? false) &&
      !dismissedKinds.includes("opportunity_swap"),
    checkIn:
      (noticeSignals?.check_in_due ?? false) &&
      !mentorMessagedToday &&
      !dismissedKinds.includes("check_in"),
    riskOfPause: noticeSignals?.risk_of_pause ?? false,
  };
}

/** Canonical mentorship → notice-signals map used by notice + homepage. */
export function buildMentorshipSignalsByUrl(
  mentorships: MentorshipDetails[],
  options: {
    now?: DateTime;
    qaScenario?: D7TriggerQaScenario | null;
    dismissedByMentorship?: DismissedKindsByMentorship;
  } = {},
): Map<string, NoticeMentorshipSignals> {
  const now = options.now ?? DateTime.now();
  const qaScenario = options.qaScenario ?? null;
  const mentorshipByUrl = new Map<string, NoticeMentorshipSignals>();

  for (const mentorship of mentorships) {
    mentorshipByUrl.set(
      mentorship.sdk_url,
      applyD7TriggerQaToNoticeSignals(
        buildNoticeMentorshipSignals(
          mentorship,
          now,
          options.dismissedByMentorship?.[mentorship.id],
        ),
        qaScenario,
      ),
    );
  }

  return mentorshipByUrl;
}
